import type { RefObject } from 'react';
import { useCallback, useState, useEffect } from 'react';

export interface UseCookieBannerBlockerHandlerArgs {
  element: RefObject<HTMLDialogElement>;
  onBlock: () => void;
}

const useCookieBannerBlockerHandler = ({
  element,
  onBlock,
}: UseCookieBannerBlockerHandlerArgs): void => {
  // We only want to detect the block once
  const [hasBlocked, setHasBlocked] = useState(false);

  const handleBlock = useCallback(() => {
    setHasBlocked(true);
    onBlock();
  }, [onBlock]);

  useEffect(() => {
    if (!element.current || hasBlocked) {
      return () => null;
    }

    const config: MutationObserverInit = {
      attributeFilter: ['style'],
      attributes: true,
    };
    const callback: MutationCallback = (mutationList, observer) => {
      mutationList.forEach((mutation) => {
        if (
          mutation.type !== 'attributes' ||
          mutation.attributeName !== 'style' ||
          !(mutation.target instanceof HTMLElement) ||
          mutation.target.style.display !== 'none'
        ) {
          return;
        }

        observer.disconnect();
        handleBlock();
      });
    };

    const observer = new MutationObserver(callback);
    observer.observe(element.current, config);

    return () => observer.disconnect();
  }, [element, hasBlocked, handleBlock]);

  if (!hasBlocked && element.current?.style.display === 'none') {
    handleBlock();
  }
};

export default useCookieBannerBlockerHandler;
